import { head } from 'ramda';
import isNil from 'lodash/isNil';

import { isBlank, isDefined } from './helpers-ts';

/**
 * Extracts and returns the wanted search param. Empty string returned if not found.
 *
 * @param {String} parameterName The search parameter to find in URL.
 * @param {String} url The URL in which to find the parameter.
 */
export const extractURLParam = (parameterName: string, url: string) => {
  try {
    const urlObj = new URL(url);
    const theParam = urlObj.searchParams.get(parameterName);

    return theParam || '';
  } catch (e) {
    return '';
  }
};

/**
 * Updates a parameters value or adds it as a new parameter.
 *
 * @param {String} parameterName To set value for
 * @param {String} value The value to set.
 * @param {Object} location the window.location object.
 */
// TODO: Don't take the location obj. Take the URL.
export const addOrChangeQueryParam = (parameterName: string, value: string | null, location: any) => {
  try {
    const urlArr = location.href.split('?');
    const origin = urlArr[0];
    const searchParams = urlArr[1];

    let searchParamString = '?';
    const allParams = searchParams ? searchParams.split('&') : [];

    // Url has search params and our parameter is included somewhere in the searchparams.
    if (searchParams && searchParams.includes(parameterName)) {
      for (let i = 0; i < allParams.length; i++) {
        const currentParam = allParams[i];
        searchParamString = currentParam.includes(parameterName)
          ? searchParamString.concat(`${parameterName}=${value}`)
          : searchParamString.concat(currentParam);
        if (i !== allParams.length - 1)
          // More params to come, append &-sign.
          searchParamString = searchParamString.concat('&');
      }
    }
    // Url has searchparams but wanted parameter name is not included.
    else if (searchParams && !searchParams.includes(parameterName)) {
      searchParamString = searchParamString.concat(`${searchParams}&${parameterName}=${value}`);
    }
    // Url doesn't have any search params.
    else {
      searchParamString = searchParamString.concat(`${parameterName}=${value}`);
    }
    return origin.concat(searchParamString);
  } catch (e) {
    return location?.href;
  }
};

/**
 * Pushes new URL to history. Does not refresh browser.
 *
 * @param {String} newUrl to push to state.
 * @param {Object} window The window object.
 */
export const pushToWindowHistory = (newUrl: string, window: any, caller = 'UNKNOWN') => {
  window.history.pushState({ path: newUrl, caller }, '', newUrl);
};

/**
 * Splits the URL by '?' and returns the first part of that array.
 *
 * @param {String} url
 */
export const removeUrlParams = (url: string) => {
  if (!isDefined(url)) return url;
  try {
    return head(url.split('?'));
  } catch (e) {
    return url;
  }
};

export const getBackendHost = (urlPath: string) => {
  const host = process.env.REACT_APP_B2B_SERVICES;
  if (host && host.trim() !== '') {
    const result = new URL(urlPath, host.trim());
    return result.toString();
  }
  return undefined;
};

export const getUrlParams = (url: string): Record<string, string> => {
  if (!url) return {};

  const urlObj = new URL(url);
  return Object.fromEntries(urlObj.searchParams);
};

export const updateUrlParams = (url: string, params: Record<string, string | number | undefined>) => {
  if (!url) return '';

  const urlObj = new URL(url);
  Object.entries(params).forEach(([key, value]) => {
    if (isNil(value)) {
      urlObj.searchParams.delete(key);
    } else {
      urlObj.searchParams.set(key, value.toString());
    }
  });
  return urlObj.toString();
};

export const cleanUrlString = (str?: string) => {
  if (isBlank(str)) return '';
  // const s = [...("eƯ‚llarfranska")].map(c => "\\u" + c.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0')).join("");
  let newName = str!
    .replace('citronlÑsk', 'citronlask')
    .replace(/G†rdsm„rkt/g, 'Gardsmarkt')
    .replace('KaG• eƯ‚llarfranska', 'Kallarfranska')
    .replace('LantbroG• eƯ‚d', 'Lantbrod')
    .replace('P†skmust', 'Paskmust')
    .replace('VAK°', 'VAK')
    .replace('C/O Lappland', 'CO_Lappland')

    .replace('%25C3%25A5', 'a')
    .replace('%25C3%25A4', 'a')
    .replace('%25C3%25B6', 'o')
    .replace(/%C3%B6/g, 'o')
    .replace(/A%CC%8A/g, 'A')
    .replace(/a%CC%88/g, 'a')
    .replace(/%C3%A4/g, 'a')
    .replace(/%C3%84/g, 'A')
    .replace(/a%CC%8A/g, 'a')
    .replace(/A%DD%88/g, 'A')
    .replace(/A%25DD%2588/g, 'A')
    .replace(/%25CC%258A/g, '')
    .replace(/a%25CC%2588/g, 'a')
    .replace(/%25C3%25A5/g, 'a')
    .replace(/%2520/g, '_')

    .replace(/%2F/g, '_')
    .replace(/%20/g, '_')
    .replace(/%26/g, '_')
    .replace(/%23/g, '_')
    .replace(/%2C/g, '_') // ,
    .replace(/,/g, '_')
    .replace(/&/g, '_')
    .replace(/ö/g, 'o')
    .replace(/ö/g, 'o')
    .replace(/ä/g, 'a')
    .replace(/ä/g, 'a')
    .replace(/·/g, '_')
    .replace(/å/g, 'a')
    .replace(/å/g, 'a')
    .replace(/Ä/g, 'A')
    .replace(/Å/g, 'A')
    .replace(/Å/g, 'A')
    .replace(/Ä/g, 'A')
    .replace(/A݊/g, 'A')
    .replace(/a݈/g, 'a')
    .replace(/o݈/g, 'o')
    .replace(/'/g, '')
    .replace(/O݈/g, 'O')
    .replace(/A݈/g, 'A')
    .replace(/:݈/g, '_')
    .replace(/:/g, '_')
    .replace(/e݁/g, 'e')
    .replace(/é݁/g, 'e')
    .replace(/é/g, 'e')
    .replace(/é/g, 'e')
    .replace(/î/g, 'i')
    .replace(/î/g, 'i')
    .replace(/Ç/g, 'e')
    .replace(/aÌˆ݁/g, 'a')
    .replace(/r݁/g, 'r')
    .replace(/Ìˆr/g, 'r')
    .replace(/\(/g, '_')
    .replace(/\)/g, '_')
    .replace(/#/g, '_')
    .replace(/\s+/g, '_')
    .replace(/Ö/g, 'O')
    .replace(/Ö/g, 'O')
    .replace(/İ/g, 'I')
    .replace(/Š/g, 'S')
    .replace(/ñ/g, 'n')
    .replace(/ú/g, 'u')
    .replace(/ü/g, 'u')
    .replace(/ü/g, 'u')
    .replace(/╠/g, 'd')
    .replace(/´/g, '_')
    .replace(/Ü/g, 'U')
    .replace(/~/g, '_')
    // .replace('ö', 'o')
    // .replace('Ä', 'a')
    // .replace('å', 'A')
    // .replace('å', 'a')
    // .replace('ä', 'a')
    // .replace('ä', 'a')
    // .replace('å', 'a')
    .replace(/è/g, 'e')
    .replace(/ê/g, 'e')
    .replace(/è/g, 'e')
    .replace(/ê/g, 'e')
    .replace(/€/g, 'E')
    .replace('[', '_')
    .replace(']', '_')
    .replace('a݊', 'a')
    // .replace('(', '_')
    // .replace(')', '_')
    // .replace(',', '_')
    // .replace('&', '_')
    .replace(/¦/g, '')
    // .replace('¦', '')
    .replace(/_+/g, '_');
  return newName;
};

export const isValidUrl = (value: string): boolean => {
  const hasInvalidCharacters = Array.from(value).some(char => {
    const code = char.charCodeAt(0);
    return code <= 0x20 || code === 0x7f;
  });

  if (!/^https?:\/\//i.test(value) || hasInvalidCharacters) {
    return false;
  }

  try {
    const url = new URL(value);

    return (url.protocol === 'http:' || url.protocol === 'https:') && Boolean(url.hostname);
  } catch {
    return false;
  }
};
